import { BriefcaseIcon, CalendarDaysIcon, FlagIcon, UserGroupIcon } from "@heroicons/react/20/solid";
import { format, isToday } from "date-fns";
import { type ComponentType, useState } from "react";

import { useDashboardCalendar } from "~/modules/dashboard/hooks/useDashboardCalendar.ts";
import type { UpcomingEvent, UpcomingEventKind } from "~/modules/dashboard/hooks/useUpcomingEvents.ts";

const VISIBLE_ROW_COUNT = 3;

const ICONS: Record<UpcomingEventKind, ComponentType<{ className?: string }>> = {
	holiday: FlagIcon,
	internalEvent: UserGroupIcon,
	absence: CalendarDaysIcon,
	staffing: BriefcaseIcon,
};

function getDateText(event: UpcomingEvent) {
	const end = event.endDate ? format(event.endDate, "dd.MM.") : null;
	if (event.startedEarlier) return `bis ${end}`;
	const start = isToday(event.date) ? "Heute" : format(event.date, "dd.MM.");
	return end ? `${start} – ${end}` : start;
}

export function UpcomingEventRows({ events }: { events: UpcomingEvent[] }) {
	const { goToDate, setView, scrollToCalendar } = useDashboardCalendar();
	const [isExpanded, setIsExpanded] = useState(false);

	const isCollapsed = events.length > VISIBLE_ROW_COUNT && !isExpanded;
	const rows = isCollapsed ? events.slice(0, VISIBLE_ROW_COUNT) : events;
	const hiddenCount = events.length - VISIBLE_ROW_COUNT;

	return (
		<ul className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
			{rows.map((event) => {
				const Icon = ICONS[event.kind];
				return (
					<li key={event.key}>
						<button type="button"
								className="flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left hover:bg-gray-100"
								onClick={() => {
									goToDate(event.date);
									setView("day");
									// On a phone the calendar is far below this section.
									scrollToCalendar();
								}}>
							<Icon className="size-4 shrink-0 text-gray-500" />
							<span className="flex min-w-0 flex-1 flex-col text-sm sm:flex-row sm:gap-1">
								<span className="font-semibold text-gray-900">{getDateText(event)}</span>
								<span className="text-gray-700">
									<span className="hidden sm:inline">· </span>{event.text}
								</span>
							</span>
						</button>
					</li>
				);
			})}
			{isCollapsed && (
				<li>
					<button type="button"
							className="flex min-h-11 w-full items-center px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-100"
							onClick={() => setIsExpanded(true)}>
						{hiddenCount === 1 ? "1 weiterer Termin" : `${hiddenCount} weitere Termine`}
					</button>
				</li>
			)}
		</ul>
	);
}
